import { useEffect, useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  locateDraftProblems,
  type Draft,
  type DraftProblem,
  type UnitsPreference,
  programRoundLabel,
} from "@heraion/shared";

import { IconButton } from "../primitives/IconButton";
import { webTapFloor, type UiTheme } from "../theme";
import { dayLabel } from "./useV3DayActions";

// «3 problems ‹ ›» under the top bar (#336, brief §9). It appears when a Save
// or a Publish is refused by the build, follows every edit from then on, and
// goes at zero, until the next refusal. Validation stays at Save: nothing
// shows while the author is simply in the middle of building.
// The list is the save's own (locateDraftProblems): ‹ › and a tap on the line
// take the author to the problem's day, exercise and round. A problem with no
// place (the program's name) stays in words, unless the top line says it.

/** The bar's state, held by the editor: Save arms it before writing. */
export function useDraftProblems(draft: Draft, cellUnit: UnitsPreference, error: string | null) {
  const [armed, setArmed] = useState(false);
  const [at, setAt] = useState(0);
  const problems = useMemo(
    () => (armed ? locateDraftProblems(draft, cellUnit) : []),
    [armed, draft, cellUnit],
  );
  // A refused Publish reaches the editor only as the shell's error line.
  useEffect(() => {
    if (error) setArmed(true);
  }, [error]);
  useEffect(() => {
    if (armed && problems.length === 0) {
      setArmed(false);
      setAt(0);
    }
  }, [armed, problems.length]);
  return { problems, at, setAt, arm: () => setArmed(true) };
}

export type DraftProblemsState = ReturnType<typeof useDraftProblems>;

export function ProblemsBar({
  theme,
  draft,
  state,
  error,
  resolveName,
  onGo,
}: {
  theme: UiTheme;
  draft: Draft;
  state: DraftProblemsState;
  /** The top line: a problem it already says is not said twice. */
  error: string | null;
  resolveName: (id: string, snapshot?: string) => string;
  onGo: (p: DraftProblem) => void;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const message = (p: DraftProblem) =>
    t(`program.errCode.${p.code}`, { defaultValue: t("program.errSchema") });
  const placed = state.problems.filter((p) => p.sIdx !== undefined);
  const unplaced = state.problems
    .filter((p) => p.sIdx === undefined)
    .map(message)
    .filter((m) => m !== error);
  if (placed.length === 0 && unplaced.length === 0) return null;

  const cur = Math.min(state.at, Math.max(0, placed.length - 1));
  const where = (p: DraftProblem) => {
    const slot = draft.sessions[p.sIdx ?? -1]?.slots.find((s) => s.id === p.slotId);
    return [
      p.sIdx !== undefined ? dayLabel(draft, p.sIdx) : null,
      slot ? resolveName(slot.exerciseId, slot.exerciseName) : null,
      p.week !== undefined ? programRoundLabel(p.week + 1) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  };
  const step = (delta: -1 | 1) => {
    const next = (cur + delta + placed.length) % placed.length;
    state.setAt(next);
    onGo(placed[next]!);
  };
  const shown = placed[cur];
  const count = placed.length;

  return (
    <View style={styles.bar}>
      {unplaced.map((m) => (
        <Text key={m} style={styles.text}>
          {m}
        </Text>
      ))}
      {shown ? (
        <>
          <View style={styles.head}>
            <Text
              style={styles.count}
              accessibilityRole="header"
              // Only the count speaks when it changes, not the whole bar.
              accessibilityLiveRegion="polite"
              aria-live="polite"
            >
              {t("editorV3.problems", { count, defaultValue: "{{count}} problems" })}
            </Text>
            <IconButton
              theme={theme}
              name="chevron-left"
              accessibilityLabel={t("editorV3.problemPrev", {
                n: ((cur - 1 + count) % count) + 1,
                total: count,
                defaultValue: "Previous problem, {{n}} of {{total}}",
              })}
              onPress={() => step(-1)}
              disabled={count < 2}
            />
            <IconButton
              theme={theme}
              name="chevron-right"
              accessibilityLabel={t("editorV3.problemNext", {
                n: ((cur + 1) % count) + 1,
                total: count,
                defaultValue: "Next problem, {{n}} of {{total}}",
              })}
              onPress={() => step(1)}
              disabled={count < 2}
            />
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("editorV3.problemGoA11y", {
              n: cur + 1,
              total: count,
              where: where(shown),
              message: message(shown),
              defaultValue: "Problem {{n}} of {{total}}, {{where}}: {{message}}. Go there",
            })}
            onPress={() => onGo(shown)}
            style={styles.line}
          >
            <Text style={styles.where}>{where(shown)}</Text>
            <Text style={styles.text}>{message(shown)}</Text>
          </Pressable>
        </>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    bar: {
      marginHorizontal: s.s5,
      marginTop: s.s3,
      padding: s.s3,
      gap: s.s2,
      borderWidth: 1,
      borderColor: p.danger,
      borderRadius: r.r3,
      backgroundColor: p.dangerFill,
    },
    head: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    count: { flex: 1, color: p.danger, fontSize: ty.sizes.base, fontWeight: "700" },
    line: { minHeight: webTapFloor ?? 44, justifyContent: "center", gap: s.s1 },
    where: { color: p.ink, fontSize: ty.sizes.sm, fontWeight: "600" },
    text: { color: p.ink, fontSize: ty.sizes.sm },
  });
}
